// frontend/src/components/masters/VendorForm.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

import './VendorForm.css'; // Component-specific styles for the form
import { openFilePreview } from '../common/FilePreviewModal';
import EmailInput from '../common/EmailInput';
import { validateFormEmails } from '../common/EmailValidation';
import Select2Dropdown from '../common/Select2Dropdown';
import { MultiSelectDropdown } from '../common/MultiSelectDropdown'; // Import MultiSelectDropdown

const API_BASE_URL = '/api/masters/vendors/';
const BANKS_API_URL = '/api/masters/banks/';
const COUNTRIES_API_URL = '/api/masters/countries/';

const GEONAMES_USERNAME = 'salimh.'; // Your GeoNames username

// Helper to get auth token
const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    // For FormData, do not set Content-Type, let the browser do it.
    return headers;
};


const VendorForm = ({ mode, initialData, onClose }) => {
    // Form state
    const [vendor, setVendor] = useState(initialData || {
        vendor_type: '', name: '', phone: '', address: 'P.O.BOX ', city: '', country: 'TZ',
        tin: '', tin_document: null, tin_na: false, vrn: '', vrn_document: null, vrn_na: false,
        contact_person: '', contact_phone: '',
        tax_invoice_has_receipt: true,
        is_local_agent: false, // New field for Vendor
        payment_modes: '', // New field for Payment Modes
        email_groups: [], attachments: [], contracts: [], // Add contracts array
        bank_details: [{ bank: '', swift_code: '', currency: 'TZS', account_number: '' }]
    });
    const [readOnly, setReadOnly] = useState(mode === 'view');
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [banks, setBanks] = useState([]);
    const [emailValidationErrors, setEmailValidationErrors] = useState({});
    const { hasPermission } = usePermissions(); // 2. Get hook
    const [vendorTypes, setVendorTypes] = useState([]);
    const [icdOptions, setIcdOptions] = useState([]);
    const [paymentModeOptions] = useState([
        'CASH',
        'BANK',
        'CONTROL NUMBER',
    ]);

    // Refs for file inputs and preview modal
    const tinDocumentRef = useRef(null);
    const vrnDocumentRef = useRef(null);
    const attachmentRefs = useRef([]); // For multiple attachment files
    const contractRefs = useRef([]); // For multiple contract files

    const formContainerRef = useRef(null); // Root element — used for DOM queries so they work inside popout windows
    const previewModalRef = useRef(null);
    const previewIframeRef = useRef(null);
    const previewImageRef = useRef(null);
    const previewDownloadDivRef = useRef(null);
    const [isMaximized, setIsMaximized] = useState(false);

    // Initial data effect
    useEffect(() => {
        if (initialData) {
            // Adjust address if it's empty but supposed to have a prefix
            const addressToSet = (!initialData.address || initialData.address === '')
                ? 'P.O.BOX '
                : initialData.address.startsWith('P.O.BOX') 
                    ? initialData.address 
                    : `P.O.BOX ${initialData.address}`;

            // Map backend attachment structure to frontend state, including file_obj for new uploads
            const initialAttachments = initialData.attachments ? initialData.attachments.map(att => ({
                ...att, // Keep existing properties like id, attachment_type
                document_file: att.document_file || null, // URL from backend
                file_obj: null // This will hold the File object for new uploads
            })) : [];

             // Map backend contracts structure to frontend state
             const initialContracts = initialData.contracts ? initialData.contracts.map(cont => ({
                ...cont,
                contract_document: cont.contract_document || null,
                file_obj: null
            })) : [];

            const countryCodeToSet = initialData.country || '';
            
            // Check if existing vendor_type is not in the default list, if so add it
            if (initialData.vendor_type) {
                const exists = vendorTypes.find(t => t.id === initialData.vendor_type);
                if (!exists) {
                    setVendorTypes(prev => [...prev, { id: initialData.vendor_type, text: initialData.vendor_type }]);
                }
            }


            setVendor(prev => ({
                ...prev,
                ...initialData,
                address: addressToSet,
                country: countryCodeToSet,
                tin_document: initialData.tin_document || null, // URL or null
                tin_na: !!initialData.tin_na,
                vrn_document: initialData.vrn_document || null, // URL or null
                vrn_na: !!initialData.vrn_na,
                tax_invoice_has_receipt: initialData.tax_invoice_has_receipt !== undefined ? initialData.tax_invoice_has_receipt : true,
                email_groups: initialData.email_groups || [], // Ensure it's an array
                attachments: initialAttachments, // Set processed attachments
                contracts: initialContracts, // Set processed contracts
                bank_details: initialData.bank_details && initialData.bank_details.length > 0 ? initialData.bank_details : [{ bank: '', swift_code: '', currency: 'TZS', account_number: '' }]
            }));
        } else {
            setVendor(prev => ({
                ...prev,
                address: 'P.O.BOX '
            }));
        }
        setReadOnly(mode === 'view');
    }, [mode, initialData]);

    // Resolve stored country name -> CCA2 once countries list is loaded; normalize city case
    useEffect(() => {
        if (countries.length > 0 && initialData?.country) {
            const found = countries.find(
                c => c.name.toUpperCase() === initialData.country.toUpperCase() || c.cca2 === initialData.country
            );
            if (found) {
                setVendor(prev => ({
                    ...prev,
                    country: found.cca2,
                    city: prev.city ? prev.city.toUpperCase() : prev.city,
                }));
            }
        }
    }, [countries, initialData]);

    // --- Geolocation, Phone, TIN, VRN Formatting ---
    const loadCountries = useCallback(async () => {
        try {
            const token = localStorage.getItem('kk_token');
            const response = await fetch(COUNTRIES_API_URL, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setCountries(Array.isArray(data) ? data : (data.results || []));
        } catch (err) {
            console.error('Failed to load countries:', err);
            setError('Failed to load countries.');
        }
    }, []);

    const loadBanks = useCallback(async () => {
        try {
            const response = await fetch(BANKS_API_URL, { headers: getAuthHeaders() });
            if (response.ok) {
                const data = await response.json();
                setBanks(data.results || data);
            } else {
                console.error("Failed to fetch banks");
            }
        } catch (err) {
            console.error("Error fetching banks:", err);
        }
    }, []);

    const loadVendorTypes = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}types/`, { headers: getAuthHeaders() });
            if (response.ok) {
                const data = await response.json();
                setVendorTypes(data);
            }
        } catch (err) {
            console.error("Error fetching vendor types:", err);
        }
    }, []);

    const loadICDs = useCallback(async () => {
        try {
            const response = await fetch('/api/masters/icds/', { headers: getAuthHeaders() });
            if (response.ok) {
                const data = await response.json();
                const list = Array.isArray(data) ? data : (data.results || []);
                setIcdOptions(list.map(icd => ({ id: icd.name, text: icd.name, short_name: icd.short_name || '' })).sort((a, b) => a.text.localeCompare(b.text)));
            }
        } catch (err) {
            console.error('Error fetching ICDs:', err);
        }
    }, []);

    const updateCities = useCallback(async (countryCode) => {
        if (!countryCode || countryCode.length !== 2) {
            setCities([]);
            return;
        }
        try {
            const response = await fetch(`https://secure.geonames.org/searchJSON?country=${countryCode}&featureClass=P&maxRows=1000&username=${GEONAMES_USERNAME}`);
            const data = await response.json();
            if (data.geonames && data.geonames.length > 0) {
                const cityList = data.geonames.map(city => city.name).sort();
                setCities(cityList);
            } else {
                setCities([]);
            }
        } catch (err) {
            console.error("Failed to load cities:", err);
            setError('Failed to load cities.');
        }
    }, []);

    const countryPhoneCodes = {
        "TZ": "+255", "KE": "+254", "UG": "+256", "RW": "+250", "BI": "+257", // Add more as needed
    };

    const formatPhoneInput = useCallback((value, country = vendor.country) => {
        const code = countryPhoneCodes[country];
        if (code) {
            let digits = value.replace(new RegExp(`^\\${code}-?`), "").replace(/\D/g, "");
            let formatted = code + "-";
            if (digits.length > 0) formatted += digits.slice(0, 3);
            if (digits.length > 3) formatted += "-" + digits.slice(3, 6);
            if (digits.length > 6) formatted += "-" + digits.slice(6, 9);
            return formatted;
        }
        // Any other country: let the user type their own country code and
        // number, capped at 15 characters total including the code.
        let v = value.replace(/[^\d+\-]/g, '');
        if (v) v = '+' + v.replace(/\+/g, '');
        return v.slice(0, 15);
    }, [vendor.country]);

    const formatTIN = useCallback((value) => {
        let digits = value.replace(/\D/g, '').slice(0, 9);
        let formatted = '';
        if (digits.length > 0) formatted += digits.slice(0, 3);
        if (digits.length > 3) formatted += '-' + digits.slice(3, 6);
        if (digits.length > 6) formatted += '-' + digits.slice(6, 9);
        return formatted;
    }, []);

    const formatVRN = useCallback((value) => {
        let val = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
        let formatted = '';
        if (val.length > 0) formatted = val.slice(0, 2);
        if (val.length > 2) formatted += '-' + val.slice(2, 8);
        if (val.length > 8) formatted += '-' + val.slice(8, 9);
        return formatted;
    }, []);

    const enforceUppercaseInputs = useCallback(() => {
        const root = formContainerRef.current;
        if (!root) return;
        root.querySelectorAll('input[type="text"], textarea').forEach(input => {
            if (!input.dataset.uppercaseListener) {
                input.addEventListener('input', function () {
                    this.value = this.value.toUpperCase();
                });
                input.dataset.uppercaseListener = "true";
            }
        });
    }, []);

    // Effects for data loading and formatting
    useEffect(() => {
        loadCountries();
        loadBanks();
        loadVendorTypes();
        loadICDs();
    }, [loadCountries, loadBanks, loadVendorTypes, loadICDs]);

    useEffect(() => {
            if (vendor.country) {
                updateCities(vendor.country); // Call updateCities when vendor.country changes
            } else {
                setCities([]); // Clear cities if country is cleared
            }
        }, [vendor.country, updateCities]);

    useEffect(() => {
        if (mode === 'add' && (!vendor.address || vendor.address === '')) {
            setVendor(prev => ({ ...prev, address: 'P.O.BOX ' }));
        }
    }, [mode, vendor.address]);


    // --- Form Field Handlers ---
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (readOnly) return;

        if (name === 'vrn_na') {
            setVendor(prev => ({
                ...prev,
                vrn_na: checked,
                vrn: checked ? '' : prev.vrn // Clear VRN if N/A checked
            }));
            return;
        }

        if (name === 'tin_na') {
            setVendor(prev => ({
                ...prev,
                tin_na: checked,
                tin: checked ? '' : prev.tin // Clear TIN if N/A checked
            }));
            return;
        }

        if (type === 'checkbox') {
            setVendor(prev => ({ ...prev, [name]: checked }));
        } else if (name === 'phone') {
            setVendor(prev => ({ ...prev, phone: formatPhoneInput(value) }));
        } else if (name === 'tin') {
            setVendor(prev => ({ ...prev, tin: formatTIN(value) }));
        } else if (name === 'vrn') {
            setVendor(prev => ({ ...prev, vrn: formatVRN(value) }));
        } else {
            // For general text inputs, ensure uppercase only if it's not handled by enforceUppercaseInputs
            // This is a fallback and might be redundant if enforceUppercaseInputs is fully effective
            if (['name', 'address', 'contact_person', 'contact_phone'].includes(name) || name.includes('group_name') || name.includes('emails')) {
                 setVendor(prev => ({ ...prev, [name]: value.toUpperCase() }));
            } else {
                 setVendor(prev => ({ ...prev, [name]: value }));
            }
        }
    };

    const handlePaymentModeChange = (selectedModes) => {
        if (readOnly) return;
        setVendor(prev => ({ ...prev, payment_modes: selectedModes }));
    };

    const handleCreateVendorType = (newTerm) => {
        const newType = { id: newTerm.toUpperCase(), text: newTerm.toUpperCase() };
        setVendorTypes(prev => [...prev, newType]);
        return newType;
    };

    const handleRepeatedChange = (e, index, fieldName, subArrayName) => {
        if (readOnly) return;
        const { value } = e.target;

        if (subArrayName === 'bank_details' && fieldName === 'bank') {
            const selectedBank = banks.find(b => b.id === parseInt(value));
            setVendor(prev => {
                const updatedArray = [...prev.bank_details];
                updatedArray[index] = { 
                    ...updatedArray[index], 
                    bank: selectedBank ? selectedBank.id : '',
                    swift_code: selectedBank ? selectedBank.swift_code : ''
                };
                return { ...prev, [subArrayName]: updatedArray };
            });
            return;
        }

        setVendor(prev => {
            const updatedArray = [...prev[subArrayName]];
            // Convert to uppercase for specific text fields within repeated sections
            let newValue = value;
            if (fieldName === 'group_name' || fieldName === 'emails' || fieldName === 'attachment_type') {
                newValue = value.toUpperCase();
            }
            updatedArray[index] = { ...updatedArray[index], [fieldName]: newValue };
            return { ...prev, [subArrayName]: updatedArray };
        });
    };

    const handleCreateContract = () => {
        setVendor(prev => ({
            ...prev,
            contracts: [...prev.contracts, { id: null, start_date: '', end_date: '', contract_document: null, remarks: '', file_obj: null }]
        }));
    };

    const removeRepeated = (index, subArrayName) => {
        if (readOnly) return;
        setVendor(prev => {
            const updatedArray = [...prev[subArrayName]];
            updatedArray.splice(index, 1);
            return { ...prev, [subArrayName]: updatedArray };
        });
    };

    // Handle files in repeated sections (Attributes, Contracts)
    const handleRepeatedFileChange = (e, index, subArrayName, fieldName, fileObjKey) => {
        if (readOnly) return;
        const file = e.target.files[0];
        if (file) {
            setVendor(prev => {
                const updated = [...prev[subArrayName]];
                updated[index] = { ...updated[index], [fieldName]: URL.createObjectURL(file), [fileObjKey]: file };
                return { ...prev, [subArrayName]: updated };
            });
        }
    }


    const getDisplayAddress = (address) => {
        // Remove P.O.BOX prefix for display in the input field
        if (!address) return '';
        return address.replace(/^P\.O\.BOX\s*/i, '').trim();
    };

    const getFullAddress = (addressValue) => {
        // Add P.O.BOX prefix when saving
        if (!addressValue) return 'P.O.BOX ';
        const cleanAddress = addressValue.replace(/^P\.O\.BOX\s*/i, '').trim();
        return cleanAddress ? `P.O.BOX ${cleanAddress}` : 'P.O.BOX ';
    };

    const handleAddressChange = (e) => {
        if (readOnly) return;
        const inputValue = e.target.value;
        // Store with P.O.BOX prefix
        const fullAddress = getFullAddress(inputValue);
        setVendor(prev => ({ ...prev, address: fullAddress }));
    };

    const handleEmailGroupChange = (value, validationState, index) => {
        setVendor(prev => {
            const updatedArray = [...prev.email_groups];
            updatedArray[index] = { ...updatedArray[index], emails: value };
            return { ...prev, email_groups: updatedArray };
        });

        // Update validation errors
        setEmailValidationErrors(prev => ({
            ...prev,
            [`email_groups_${index}_emails`]: validationState.errors
        }));
    };

    const handleFileChange = (e, fieldName, index = null) => {
        if (readOnly) return;
        const file = e.target.files[0];
        if (!file) return;

        // Reset the input value to allow selecting the same file again after clearing
        e.target.value = '';

        if (fieldName === 'tin_document' || fieldName === 'vrn_document') {
            setVendor(prev => ({ ...prev, [fieldName]: file }));
        } else if (fieldName === 'attachments' && index !== null) {
            setVendor(prev => {
                const updatedAttachments = [...prev.attachments];
                updatedAttachments[index] = { ...updatedAttachments[index], file_obj: file, document_file: URL.createObjectURL(file) };
                return { ...prev, attachments: updatedAttachments };
            });
        }
    };

    const clearFile = (fieldName, index = null) => {
        if (readOnly) return;
        if (fieldName === 'tin_document' || fieldName === 'vrn_document') {
            setVendor(prev => ({ ...prev, [fieldName]: null }));
            if (tinDocumentRef.current) tinDocumentRef.current.value = '';
            if (vrnDocumentRef.current) vrnDocumentRef.current.value = '';
        } else if (fieldName === 'attachments' && index !== null) {
            setVendor(prev => {
                const updatedAttachments = [...prev.attachments];
                updatedAttachments[index] = { ...updatedAttachments[index], file_obj: null, document_file: null }; // Clear both
                if (attachmentRefs.current[index]) attachmentRefs.current[index].value = ''; // Clear file input
                return { ...prev, attachments: updatedAttachments };
            });
        }
    };

    // --- Repeatable Section Logic ---
    const addRepeatedItem = (subArrayName, newItem) => {
        if (readOnly) return;
        setVendor(prev => ({
            ...prev,
            [subArrayName]: [...prev[subArrayName], newItem]
        }));
    };

    const removeRepeatedItem = (subArrayName, index) => {
        if (readOnly) return;
        setVendor(prev => {
            const updatedArray = prev[subArrayName].filter((_, i) => i !== index);
            return { ...prev, [subArrayName]: updatedArray };
        });
    };

    // --- File Preview Modal Logic (reused from CustomerForm) ---
    const openPreview = (fileURL, fileName) => {
        openFilePreview(fileURL, fileName, {
            customTitle: `Preview: ${fileName || 'Document'}`,
            onDownload: (data) => {
                // Custom download handler if needed
                console.log('Download requested:', data);
            },
            onClose: () => {
                // Custom close handler if needed
                console.log('Preview window closed');
            }
        });
    };
    const closePreview = () => {
        if (previewModalRef.current && previewIframeRef.current && previewImageRef.current && previewDownloadDivRef.current) {
            previewModalRef.current.style.display = 'none';
            previewIframeRef.current.src = '';
            previewImageRef.current.src = '';
            previewDownloadDivRef.current.style.display = 'none';
            setIsMaximized(false); // Reset maximize state
            Object.assign(previewModalRef.current.style, {
                top: previewModalRef.current.dataset.originalTop || '50px',
                left: previewModalRef.current.dataset.originalLeft || '50px',
                width: previewModalRef.current.dataset.originalWidth || '80%',
                height: previewModalRef.current.dataset.originalHeight || '80%',
                resize: 'both'
            });
        }
    };

    const toggleMaximize = () => {
        if (!previewModalRef.current) return;
        const modal = previewModalRef.current;
        const btn = modal.querySelector('#maximizeBtn');

        if (!isMaximized) {
            modal.dataset.originalLeft = modal.style.left;
            modal.dataset.originalTop = modal.style.top;
            modal.dataset.originalWidth = modal.style.width;
            modal.dataset.originalHeight = modal.style.height;

            modal.style.top = '0';
            modal.style.left = '0';
            modal.style.width = '100vw';
            modal.style.height = '100vh';
            modal.style.resize = 'none';
            btn.textContent = 'Restore';
        } else {
            modal.style.top = modal.dataset.originalTop;
            modal.style.left = modal.dataset.originalLeft;
            modal.style.width = modal.dataset.originalWidth;
            modal.style.height = modal.dataset.originalHeight;
            modal.style.resize = 'both';
            btn.textContent = 'Maximize';
        }
        setIsMaximized(!isMaximized);
    };

    // Make modal draggable
    useEffect(() => {
        const modal = previewModalRef.current;
        if (!modal) return;
        const ownerDoc = modal.ownerDocument || document;
        const header = modal.querySelector('#modalHeader');
        let isDragging = false;
        let offsetX, offsetY;

        const onMouseDown = (e) => {
            isDragging = true;
            offsetX = e.clientX - modal.offsetLeft;
            offsetY = e.clientY - modal.offsetTop;
            ownerDoc.body.style.userSelect = 'none';
        };
        const onMouseUp = () => {
            isDragging = false;
            ownerDoc.body.style.userSelect = 'auto';
        };
        const onMouseMove = (e) => {
            if (isDragging) {
                modal.style.left = (e.clientX - offsetX) + 'px';
                modal.style.top = (e.clientY - offsetY) + 'px';
            }
        };

        if (header) {
            header.addEventListener('mousedown', onMouseDown);
            ownerDoc.addEventListener('mouseup', onMouseUp);
            ownerDoc.addEventListener('mousemove', onMouseMove);
        }

        return () => {
            if (header) {
                header.removeEventListener('mousedown', onMouseDown);
                ownerDoc.removeEventListener('mouseup', onMouseUp);
                ownerDoc.removeEventListener('mousemove', onMouseMove);
            }
        };
    }, []);

    // --- Form Submission ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) return;

        setError(null);
        setMessage(null);

        // Validate Payment Modes
        if (vendor.payment_modes && vendor.payment_modes.includes('BANK')) {
             if (!vendor.bank_details || vendor.bank_details.length === 0 || vendor.bank_details.some(b => !b.bank || !b.account_number)) {
                 setError('Bank details are required when Payment Mode "Bank" is selected.');
                 toast.error('Bank details are required when Payment Mode "Bank" is selected.');
                 return;
             }
        }

        const formData = new FormData();

        // Validate all email groups
        const emailFields = vendor.email_groups.map((group, index) => ({
            fieldPath: `email_groups.${index}.emails`,
            required: false, // Set to true if you want to make emails required
            label: `Email Group ${index + 1}`
        }));

        const emailValidation = validateFormEmails(vendor, emailFields);
        
        if (!emailValidation.isValid) {
            setEmailValidationErrors(emailValidation.errors);
            setError('Please fix email validation errors before submitting.');
            toast.error('Please fix email validation errors before submitting.');
            return;
        }

        // Clear email validation errors if all are valid
        setEmailValidationErrors({});
        // Append simple fields
        for (const key in vendor) {
            // Skip file objects and nested arrays for now, they're handled separately
            if (['email_groups', 'attachments', 'tin_document', 'vrn_document', 'contracts', 'payment_modes', 'bank_details'].includes(key)) {
                continue;
            }
            if (typeof vendor[key] === 'boolean') {
                formData.append(key, vendor[key] ? 'true' : 'false');
            } else if (vendor[key] !== null && vendor[key] !== undefined) {
                if (key === 'country') {
                    const countryObject = countries.find(c => c.cca2 === vendor[key]);
                    if (countryObject) {
                        formData.append(key, countryObject.name); // Send full country name
                    } else {
                        formData.append(key, vendor[key]); // Fallback to CCA2 if name not found
                    }
                } else {
                    formData.append(key, vendor[key]);
                }
            }
        }

        // Append Payment Modes
        if (vendor.payment_modes) {
            const modes = Array.isArray(vendor.payment_modes) ? vendor.payment_modes.join(',') : vendor.payment_modes;
            formData.append('payment_modes', modes);
        }

        // Append file fields
        if (vendor.tin_document instanceof File) {
            formData.append('tin_document', vendor.tin_document);
        } else if (typeof vendor.tin_document === 'string' && vendor.tin_document !== '') {
            // If it's an existing file (URL string), send its URL back if backend needs to preserve it
            formData.append('tin_document_url', vendor.tin_document);
        }
        formData.append('tin_na', vendor.tin_na ? 'true' : 'false');

        if (vendor.vrn_document instanceof File) {
            formData.append('vrn_document', vendor.vrn_document);
        } else if (typeof vendor.vrn_document === 'string' && vendor.vrn_document !== '') {
            // If it's an existing file (URL string), send its URL back if backend needs to preserve it
            formData.append('vrn_document_url', vendor.vrn_document);
        }
        formData.append('vrn_na', vendor.vrn_na ? 'true' : 'false');

        // Append nested arrays as JSON strings
        formData.append('email_groups', JSON.stringify(vendor.email_groups));
        formData.append('bank_details', JSON.stringify(vendor.bank_details));

        // Append attachments
        if (vendor.attachments) {
            vendor.attachments.forEach((attachment, index) => {
                // If a new file object is present, append it
                if (attachment.file_obj instanceof File) {
                    formData.append(`attachments[${index}][document_file]`, attachment.file_obj);
                    formData.append(`attachments[${index}][attachment_type]`, attachment.attachment_type || '');
                    // If it's an existing attachment being updated with a new file, include its ID
                    if (attachment.id) {
                        formData.append(`attachments[${index}][id]`, attachment.id);
                    }
                }
                // If it's an existing attachment and no new file is uploaded, but there's an existing URL,
                // send the URL back to preserve the attachment on the backend.
                else if (typeof attachment.document_file === 'string' && attachment.document_file !== '') {
                    formData.append(`attachments[${index}][document_file_url]`, attachment.document_file);
                    formData.append(`attachments[${index}][attachment_type]`, attachment.attachment_type || '');
                    if (attachment.id) {
                        formData.append(`attachments[${index}][id]`, attachment.id);
                    }
                }
            });
        }

        // Append Contracts (New)
        if (vendor.contracts) {
            vendor.contracts.forEach((contract, index) => {
                if (contract.file_obj instanceof File) {
                    formData.append(`contracts[${index}][contract_document]`, contract.file_obj);
                } else if (typeof contract.contract_document === 'string' && contract.contract_document !== '') {
                    formData.append(`contracts[${index}][contract_document_url]`, contract.contract_document);
                }

                formData.append(`contracts[${index}][start_date]`, contract.start_date || '');
                formData.append(`contracts[${index}][end_date]`, contract.end_date || '');
                if (contract.id) {
                    formData.append(`contracts[${index}][id]`, contract.id);
                }
            });
        }


        // Log FormData contents for debugging
        console.log("--- FormData contents before sending (VendorForm) ---");
        for (let pair of formData.entries()) {
            if (pair[1] instanceof File) {
                console.log(pair[0]+ ': File - ' + pair[1].name);
            } else {
                console.log(pair[0]+ ': ' + pair[1]);
            }
        }
        console.log("-----------------------------------------------------");

        const url = initialData ? `${API_BASE_URL}${initialData.id}/` : API_BASE_URL;
        const method = initialData ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method: method,
                headers: getAuthHeaders(true),
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = Object.entries(errorData).map(([key, val]) => `${key}: ${val}`).join('; ');
                setError(`Save failed: ${errorMessage}`);
                toast.error(`Save failed: ${errorMessage}`);
                return;
            }

            const savedData = await response.json().catch(() => null);
            toast.success('Vendor saved successfully!');
            if (onClose) onClose(true, savedData);

        } catch (err) {
            setError('Failed to save Vendor. Network error or server unreachable.');
            toast.error('Failed to save Vendor. Network error or server unreachable.');
        }
    };

    const handleCancel = () => {
        onClose(false); // Notify parent to close without refreshing list
    };

    // Log the current mode and readOnly state directly before render for debugging
    console.log(`[RENDER] Current mode: "${mode}", readOnly state: "${readOnly}"`);
    console.log(`[RENDER] Vendor state:`, vendor);

    return (
        <div className="vendor-form-container" ref={formContainerRef}>
            <h1>Vendor Details</h1>
            <div className="toggle-buttons">
                <button type="button" onClick={() => formContainerRef.current?.querySelectorAll('.section-content').forEach(el => el.style.display = 'block')}>Expand All</button>
                <button type="button" onClick={() => formContainerRef.current?.querySelectorAll('.section-content').forEach(el => el.style.display = 'none')}>Collapse All</button>
            </div>

            {error && <div className="form-error">{error}</div>}
            {message && <div className="form-success">{message}</div>}

            <form onSubmit={handleSubmit}>
                {/* Vendor Information */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Vendor Information</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div>
                                <label className="required">Type</label>
                                <Select2Dropdown
                                    options={vendorTypes}
                                    value={vendor.vendor_type}
                                    onChange={(val) => setVendor(prev => ({ ...prev, vendor_type: val }))}
                                    valueKey="id"
                                    displayKey="text"
                                    placeholder="Select Type"
                                    disabled={readOnly}
                                />
                            </div>
                            <div>
                                <label className="required">Payment Modes</label>
                                <MultiSelectDropdown
                                    options={paymentModeOptions}
                                    selectedValues={vendor.payment_modes ? (Array.isArray(vendor.payment_modes) ? vendor.payment_modes : vendor.payment_modes.split(',')) : []}
                                    onChange={(val) => handlePaymentModeChange(val)}
                                    placeholder="Select Payment Modes"
                                    readOnly={readOnly}
                                />
                            </div>                       
                        </div>
                        <div className="row" style={{ gridTemplateColumns: '2fr 1fr 1.5fr' }}>
                            <div>
                                <label className="required">Name</label>
                                {vendor.vendor_type?.toUpperCase() === 'ICD' && !readOnly ? (
                                    <Select2Dropdown
                                        options={icdOptions}
                                        value={vendor.name}
                                        onChange={(val) => {
                                            const selected = icdOptions.find(o => o.id === val);
                                            setVendor(prev => ({
                                                ...prev,
                                                name: val || '',
                                                short_name: selected ? selected.short_name : prev.short_name,
                                            }));
                                        }}
                                        valueKey="id"
                                        displayKey="text"
                                        placeholder="Select ICD"
                                    />
                                ) : (
                                    <input type="text" name="name" value={vendor.name} onChange={handleChange} required readOnly={readOnly} />
                                )}
                            </div>
                            <div>
                                <label>Short Name</label>
                                <input type="text" name="short_name" value={vendor.short_name || ''} onChange={handleChange} readOnly={readOnly} />
                            </div>
                            <div>
                                <label className="required">Phone</label>
                                <input type="text" name="phone" value={vendor.phone} onChange={handleChange} required readOnly={readOnly} placeholder={countryPhoneCodes[vendor.country] ? `${countryPhoneCodes[vendor.country]}-___-___-___` : '+CC PHONE NUMBER'} maxLength={vendor.country ? (countryPhoneCodes[vendor.country] ? countryPhoneCodes[vendor.country].length + 12 : 15) : 17} />
                            </div>
                        </div>
                        <div className="row">                            
                            <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '20px' }}>
                                <div>
                                    <label className="required">Address</label>
                                    <div className={`address-input-container ${readOnly ? 'readonly' : ''}`}>
                                        <span className="address-prefix">P.O.BOX</span>
                                        <input 
                                            type="text" 
                                            name="address" 
                                            value={getDisplayAddress(vendor.address)}
                                            onChange={handleAddressChange}
                                            required 
                                            readOnly={readOnly}
                                            className="address-input"
                                            placeholder="Enter address details..."
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="required">Country</label>
                                    <Select2Dropdown
                                        options={countries.map(c => ({ id: c.cca2, text: c.name }))}
                                        value={vendor.country}
                                        onChange={value => {
                                            setVendor(prev => {
                                                // Strip any previously applied country code, then
                                                // reformat the remaining digits under the new country.
                                                let digits = (prev.phone || '').replace(/\D/g, '');
                                                const knownCodes = Object.values(countryPhoneCodes).map(c => c.slice(1));
                                                const matched = knownCodes.find(c => digits.startsWith(c));
                                                if (matched) digits = digits.slice(matched.length);
                                                return { ...prev, country: value, phone: formatPhoneInput(digits, value) };
                                            });
                                            updateCities(value);
                                        }}
                                        placeholder="SELECT COUNTRY"
                                        displayKey="text"
                                        valueKey="id"
                                        required
                                        disabled={readOnly}
                                        clearable={true}
                                    />
                                </div>
                                <div>
                                    <label className="required">City</label>
                                    <Select2Dropdown
                                        options={cities.map(c => ({ id: c.toUpperCase(), text: c.toUpperCase() }))}
                                        value={vendor.city}
                                        onChange={value => setVendor(prev => ({ ...prev, city: value }))}
                                        placeholder="SELECT CITY"
                                        displayKey="text"
                                        valueKey="id"
                                        required
                                        disabled={readOnly}
                                        clearable={true}
                                    />
                                </div>
                            </div>
                            <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 2fr 2fr 1fr', gap: '20px' }}>
                                <div>
                                    <label className="required">TIN</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input 
                                            type="text" 
                                            name="tin" 
                                            value={vendor.tin} 
                                            onChange={handleChange} 
                                            required={!vendor.tin_na} 
                                            readOnly={readOnly || vendor.tin_na} 
                                            pattern="\d{3}-\d{3}-\d{3}" 
                                            maxLength="11" 
                                            placeholder="123-456-789"
                                            style={{ flex: 1 }} 
                                        />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'normal', whiteSpace: 'nowrap' }}>
                                            <input
                                                type="checkbox"
                                                name="tin_na"
                                                checked={!!vendor.tin_na}
                                                onChange={handleChange}
                                                disabled={readOnly}
                                                style={{ marginLeft: '8px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                                            />
                                            N/A
                                        </label>
                                    </div>
                                </div>
                                <div>
                                    <label>TIN Document Upload</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input type="file" ref={tinDocumentRef} onChange={(e) => handleFileChange(e, 'tin_document')} accept=".pdf,.jpg,.png,.doc,.docx" disabled={readOnly} style={{ width: '200px' }} />
                                        {(vendor.tin_document || initialData?.tin_document) && ( // Check both current state and initialData
                                            <div className="file-links" style={{ whiteSpace: 'nowrap', display: 'inline' }}>
                                                {typeof vendor.tin_document === 'string' && vendor.tin_document && <a href={vendor.tin_document} target="_blank" rel="noopener noreferrer" className="preview-link">View Existing</a>}
                                                {vendor.tin_document instanceof File && <span onClick={() => openPreview(URL.createObjectURL(vendor.tin_document), vendor.tin_document.name)} className="preview-link">View Uploaded</span>}
                                                {vendor.tin_document instanceof File && <a href={URL.createObjectURL(vendor.tin_document)} download={vendor.tin_document.name}>Download</a>}
                                                {!readOnly && (
                                                    <button type="button" onClick={() => clearFile('tin_document')} className="clear-file-btn">Clear</button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label>Contact Person</label>
                                    <input type="text" name="contact_person" value={vendor.contact_person} onChange={handleChange} readOnly={readOnly} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', marginTop: '30px' }}>
                                    <label htmlFor="tax_invoice_has_receipt" style={{ marginRight: '10px', whiteSpace: 'nowrap' }}>Tax Invoice Has Receipt</label>
                                    <input
                                        type="checkbox"
                                        name="tax_invoice_has_receipt"
                                        id="tax_invoice_has_receipt"
                                        checked={!!vendor.tax_invoice_has_receipt}
                                        onChange={handleChange}
                                        disabled={readOnly}
                                        style={{ width: 'auto', margin: 0 }}
                                    />
                                </div>
                            </div>
                            <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 2fr 2fr 1fr', gap: '20px' }}>
                                <div>
                                    <label className="required">VRN</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input
                                            type="text"
                                            name="vrn"
                                            value={vendor.vrn}
                                            onChange={handleChange}
                                            required={!vendor.vrn_na}
                                            readOnly={readOnly || vendor.vrn_na}
                                            pattern="\d{2}-\d{6}-[A-Z]"
                                            maxLength="11"
                                            placeholder="12-345678-A"
                                        />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'normal' }}>
                                            <input
                                                type="checkbox"
                                                name="vrn_na"
                                                checked={!!vendor.vrn_na}
                                                onChange={handleChange}
                                                disabled={readOnly}
                                                style={{ marginLeft: '8px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                                            />
                                            N/A
                                        </label>
                                    </div>
                                </div>
                                <div>
                                    <label>VRN Document Upload</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input type="file" ref={vrnDocumentRef} onChange={(e) => handleFileChange(e, 'vrn_document')} accept=".pdf,.jpg,.png,.doc,.docx" disabled={readOnly} style={{ width: '200px' }} />
                                        {(vendor.vrn_document || initialData?.vrn_document) && ( // Check both current state and initialData
                                            <div className="file-links" style={{ whiteSpace: 'nowrap', display: 'inline' }}>
                                                {typeof vendor.vrn_document === 'string' && vendor.vrn_document && <a href={vendor.vrn_document} target="_blank" rel="noopener noreferrer" className="preview-link">View Existing</a>}
                                                {vendor.vrn_document instanceof File && <span onClick={() => openPreview(URL.createObjectURL(vendor.vrn_document), vendor.vrn_document.name)} className="preview-link">View Uploaded</span>}
                                                {vendor.vrn_document instanceof File && <a href={URL.createObjectURL(vendor.vrn_document)} download={vendor.vrn_document.name}>Download</a>}
                                                {!readOnly && (
                                                    <button type="button" onClick={() => clearFile('vrn_document')} className="clear-file-btn">Clear</button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label>Contact Phone</label>
                                    <input type="text" name="contact_phone" value={vendor.contact_phone} onChange={handleChange} readOnly={readOnly} />
                                </div>
                                <div></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vendor Email Groups */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Vendor Email Groups</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="repeatable" id="emailGroups">
                            {vendor.email_groups.map((group, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '1' }}><label className="required">Email Group</label><input type="text" value={group.group_name || ''} onChange={(e) => handleRepeatedChange(e, index, 'group_name', 'email_groups')} required readOnly={readOnly} /></div>
                                    <div style={{ flex: '2' }}><label className="required">Emails</label>
                                        <EmailInput
                                            value={group.emails}
                                            onChange={(value, validationState) => handleEmailGroupChange(value, validationState, index)}
                                            placeholder="Email addresses (separate with comma or semicolon)"
                                            readOnly={readOnly}
                                            required={false} // Set to true if you want to make emails required
                                            showValidationStatus={false}
                                            showHelpText={true}
                                        />
                                    </div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('email_groups', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('email_groups', { group_name: '', emails: '' })}>Add Email Group</button>}
                    </div>
                </div>

                {/* Bank Details Section */}
                {(Array.isArray(vendor.payment_modes) ? vendor.payment_modes.includes('BANK') : (vendor.payment_modes && vendor.payment_modes.includes('BANK'))) && (
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Bank Details</h2>
                    <div className="section-content">
                        <div className="repeatable" id="bank_details">
                            {vendor.bank_details.map((detail, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '2' }}>
                                        <label className="required">Bank</label>
                                        <Select2Dropdown
                                            options={banks.map(b => ({ id: b.id, text: b.name }))}
                                            value={detail.bank}
                                            onChange={val => handleRepeatedChange({ target: { value: val } }, index, 'bank', 'bank_details')}
                                            valueKey="id"
                                            displayKey="text"
                                            placeholder="Select Bank"
                                            disabled={readOnly}
                                        />
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <label>Swift Code</label>
                                        <input type="text" value={detail.swift_code || ''} readOnly placeholder="Auto-populated" />
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <label className="required">Currency</label>
                                        <Select2Dropdown
                                            options={majorCurrencies}
                                            value={detail.currency}
                                            onChange={val => handleRepeatedChange({ target: { value: val } }, index, 'currency', 'bank_details')}
                                            valueKey="id"
                                            displayKey="text"
                                            placeholder="Select Currency"
                                            disabled={readOnly}
                                        />
                                    </div>
                                    <div style={{ flex: '2' }}>
                                        <label className="required">Account Number</label>
                                        <input type="text" value={detail.account_number || ''} onChange={(e) => handleRepeatedChange(e, index, 'account_number', 'bank_details')} required readOnly={readOnly} />
                                    </div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('bank_details', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('bank_details', { bank: '', swift_code: '', currency: 'TZS', account_number: '' })}>Add Bank Detail</button>}
                    </div>
                </div>
                )}

                {/* Contract Details */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Contract Details</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="repeatable" id="contracts">
                            {vendor.contracts && vendor.contracts.map((contract, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '1' }}>
                                        <label>Start Date</label>
                                        <input 
                                            type="date" 
                                            value={contract.start_date || ''} 
                                            onChange={(e) => handleRepeatedChange(e, index, 'start_date', 'contracts')} 
                                            readOnly={readOnly} 
                                        />
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <label>End Date</label>
                                        <input 
                                            type="date" 
                                            value={contract.end_date || ''} 
                                            onChange={(e) => handleRepeatedChange(e, index, 'end_date', 'contracts')} 
                                            readOnly={readOnly} 
                                        />
                                    </div>
                                    <div style={{ flex: '2' }}>
                                        <label className="required">Contract Document</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <input 
                                                type="file" 
                                                ref={el => contractRefs.current[index] = el}
                                                onChange={(e) => handleRepeatedFileChange(e, index, 'contracts', 'contract_document_url', 'file_obj')} 
                                                accept=".pdf,.jpg,.png,.doc,.docx" 
                                                disabled={readOnly} 
                                                style={{ width: '200px' }} 
                                            />
                                            {contract.file_obj instanceof File && (
                                                <span onClick={() => openPreview(URL.createObjectURL(contract.file_obj), contract.file_obj.name)} className="preview-link">View Uploaded</span>
                                            )}
                                            {typeof contract.contract_document === 'string' && contract.contract_document && !(contract.file_obj instanceof File) && (
                                                <a href={contract.contract_document} target="_blank" rel="noopener noreferrer" className="preview-link">View Existing</a>
                                            )}
                                            {contract.file_obj instanceof File && <a href={URL.createObjectURL(contract.file_obj)} download={contract.file_obj.name}>Download</a>}
                                            {typeof contract.contract_document === 'string' && contract.contract_document && !(contract.file_obj instanceof File) && (
                                                <a href={contract.contract_document} download={contract.contract_document.split('/').pop()}>Download</a>
                                            )}
                                            {!readOnly && (contract.file_obj || (typeof contract.contract_document === 'string' && contract.contract_document)) && (
                                                <button type="button" onClick={() => {
                                                    setVendor(prev => {
                                                        const updated = [...prev.contracts];
                                                        updated[index] = { ...updated[index], contract_document: null, file_obj: null };
                                                        if (contractRefs.current[index]) contractRefs.current[index].value = '';
                                                        return { ...prev, contracts: updated };
                                                    });
                                                }} className="clear-file-btn">Clear</button>
                                            )}
                                        </div>
                                    </div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('contracts', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={handleCreateContract}>Add Contract</button>}
                    </div>
                </div>

                {/* Attachments */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Attachments</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="repeatable" id="attachments">
                            {vendor.attachments.map((attachment, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '0.5', minWidth: '90px', marginRight: '8px' }}>
                                        <label className="required">Type</label>
                                        <input type="text" value={attachment.attachment_type || ''} onChange={(e) => handleRepeatedChange(e, index, 'attachment_type', 'attachments')} required readOnly={readOnly} placeholder="Enter document type" />
                                    </div>
                                    <div style={{ flex: '3' }}>
                                        <label className="required">Document Upload</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <input type="file" ref={el => attachmentRefs.current[index] = el} onChange={(e) => handleFileChange(e, 'attachments', index)} accept=".pdf,.jpg,.png,.doc,.docx" disabled={readOnly} style={{ width: '200px' }} />
                                            {/* Display based on whether it's a new file (file_obj) or an existing one (document_file URL) */}
                                            {/* Show "View Uploaded" if a new file has been selected */}
                                            {attachment.file_obj instanceof File && (
                                                <span onClick={() => openPreview(URL.createObjectURL(attachment.file_obj), attachment.file_obj.name)} className="preview-link">View Uploaded</span>
                                            )}
                                            {/* Show "View Existing" if there's an existing file URL and no new file selected */}
                                            {typeof attachment.document_file === 'string' && attachment.document_file && !(attachment.file_obj instanceof File) && (
                                                <a href={attachment.document_file} target="_blank" rel="noopener noreferrer" className="preview-link">View Existing</a>
                                            )}
                                            {/* Show "Download" for both cases */}
                                            {attachment.file_obj instanceof File && <a href={URL.createObjectURL(attachment.file_obj)} download={attachment.file_obj.name}>Download</a>}
                                            {typeof attachment.document_file === 'string' && attachment.document_file && !(attachment.file_obj instanceof File) && (
                                                <a href={attachment.document_file} download={attachment.document_file.split('/').pop()}>Download</a>
                                            )}
                                            {!readOnly && (attachment.file_obj || (typeof attachment.document_file === 'string' && attachment.document_file)) && (
                                                <button type="button" onClick={() => clearFile('attachments', index)} className="clear-file-btn">Clear</button>
                                            )}
                                        </div>
                                    </div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('attachments', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('attachments', { attachment_type: '', document_file: null, file_obj: null })}>Add Attachment</button>}
                    </div>
                </div>

                <div className="buttons">
                    {/* 3. Protect Edit button */}
                    {readOnly && hasPermission('VENDORS', 'can_edit') && (
                        <button type="button" onClick={() => setReadOnly(false)}>Edit</button>
                    )}
                    {/* 4. Protect Save button */}
                    {!readOnly && (
                        <>
                            <button type="submit">Save</button>
                            <button type="button" onClick={handleCancel} className="cancel-btn">Cancel</button>
                        </>
                    )}
                </div>
            </form>

            {/* File Preview Modal */}
            <div id="previewModal" ref={previewModalRef} style={{ display: 'none', position: 'fixed', top: '50px', left: '50px', width: '80%', height: '80%', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '8px', zIndex: '9999', boxShadow: '0 0 10px rgba(0,0,0,0.5)', resize: 'both', overflow: 'auto' }}>
                <div id="modalHeader" style={{ cursor: 'move', background: '#f1f1f1', padding: '10px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>File Preview</span>
                    <div>
                        <button id="maximizeBtn" onClick={toggleMaximize} style={{ fontSize: '12px', marginRight: '5px' }}>Maximize</button>
                        <button onClick={closePreview} style={{ fontSize: '12px' }}>Close</button>
                    </div>
                </div>
                <iframe id="previewFrame" ref={previewIframeRef} style={{ width: '100%', height: 'calc(100% - 40px)', border: 'none', display: 'none' }} title="File Preview"></iframe>
                <img id="previewImage" ref={previewImageRef} style={{ maxWidth: '100%', maxHeight: 'calc(100% - 40px)', display: 'none', margin: '0 auto' }} alt="Preview" />
                <div id="downloadDiv" ref={previewDownloadDivRef} style={{ padding: '20px', textAlign: 'center', display: 'none' }}></div>
            </div>
        </div>
    );
};

const majorCurrencies = [  
        { id: 'EUR', text: 'EUR - Euro' },
        { id: 'GBP', text: 'GBP - British Pound Sterling' },
        { id: 'TZS', text: 'TZS - Tanzanian Shilling' },
        { id: 'USD', text: 'USD - United States Dollar' },
    ];

export default VendorForm;
